import { Button } from "@/components/ui/button";
import {
  DialogFooter
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import { useReprocessConciliationAsyncMutation } from "@/services/store-conciliation";
import { AlertCircle, CheckCircle2, Loader2, Mail } from "lucide-react";
import { useState } from "react";

interface AsyncConfirmationFlowProps {
  step: 'confirm' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
  onClose: () => void;
  transactionId: string;
  onJobCreated?: () => void;
}

export const AsyncConfirmationFlow = ({
  step,
  onConfirm,
  onCancel,
  onClose,
  transactionId,
  onJobCreated,
}: AsyncConfirmationFlowProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [reprocessConciliationAsync] = useReprocessConciliationAsyncMutation();

  const handleConfirmAsync = () => {
    if (!transactionId) {
      console.error("Sem id de transacao!");
      toast({
        variant: "destructive",
        description: "Erro: ID de transacao nao encontrado",
        duration: 5000,
      });
      return;
    }

    setIsLoading(true);

    reprocessConciliationAsync({ id: parseInt(transactionId, 10) })
      .unwrap()
      .then(() => {
        onJobCreated?.();
        toast({
          variant: "success",
          description: "Reprocessamento iniciado em segundo plano!",
          duration: 5000,
        });
        onConfirm();
      })
      .catch((error: any) => {
        console.error(error);
        toast({
          variant: "destructive",
          description: "Erro ao iniciar reprocessamento!",
          duration: 5000,
        });
      })
      .finally(() => setIsLoading(false));
  };

  if (step === 'confirm')
    return (
      <>
        <div className="py-1 space-y-4">
          <h3>
            Você tem certeza que deseja reprocessar em segundo plano?
          </h3>
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900">
              <p className="font-medium">Processamento em segundo plano</p>
              <p className="text-blue-700 mt-1">
                O reprocessamento sera executado em background. Voce pode continuar trabalhando e recebera uma notificacao quando o processo for concluido.
              </p>
            </div>
          </div>
        </div>
        <DialogFooter className="sm:justify-between gap-3 pt-5">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isLoading}
            className="flex-1 sm:flex-none"
          >
            Voltar
          </Button>
          <Button
            onClick={handleConfirmAsync}
            disabled={isLoading}
            className="flex-1 sm:flex-none"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Confirmar
              </>
            )}
          </Button>
        </DialogFooter>
      </>
    );

  return (
    <>
      <div className="py-1 space-y-4">
        <span>
          O reprocessamento foi iniciado com sucesso.
        </span>
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <CheckCircle2 className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-medium">Processo em execucao</p>
            <p className="text-blue-700 mt-1">
              O reprocessamento esta sendo executado em segundo plano. Voce pode continuar navegando pelo sistema normalmente.
            </p>
          </div>
        </div>
        <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <Mail className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-900">
            <p className="font-medium">Notificações</p>
            <p className="text-blue-700 mt-1">
              Voce recebera uma notificacao quando o processo for concluido.
            </p>
          </div>
        </div>
      </div>
      <DialogFooter className="sm:justify-center gap-3 pt-5">
        <Button
          onClick={onClose}
          variant="outline"
          className="w-full sm:w-auto"
        >
          Fechar
        </Button>
      </DialogFooter>
    </>
  );
};
